import type * as amplitude from '@amplitude/analytics-browser';
import type { Identify } from '@amplitude/analytics-node';
import type { ExperimentUser } from '@amplitude/experiment-js-client';
import { logger } from '@magicschool/logger';
import isNil from 'lodash-es/isNil';
import omitBy from 'lodash-es/omitBy';
import { MILLISECONDS_IN_DAY } from '../date';
import type { AnalyticsUserProperties, SharedAnalyticsUserPropertyArgs } from './types';

type BuildAmplitudeUserProperties = SharedAnalyticsUserPropertyArgs & {
  cachedProperties?: Partial<AnalyticsUserProperties>;
};

export const getUserAgeDays = (createdAt: string | null | undefined) => {
  if (!createdAt) throw new Error('No createdAt date provided for user!');
  const createdAtDate = new Date(createdAt);
  if (isNaN(createdAtDate.getTime())) throw new Error('Invalid createdAt date provided for user!');

  return Math.floor((Date.now() - createdAtDate.getTime()) / MILLISECONDS_IN_DAY);
};

export const buildAmplitudeUserProperties = ({
  user,
  teacherProfile,
  appTheme,
  userSystemTheme,
  cachedProperties,
}: BuildAmplitudeUserProperties) => {
  const teacherProfileFields = teacherProfile
    ? {
        role: teacherProfile.profession?.name ?? teacherProfile.profession_id ?? null,
        subjects: (teacherProfile.subjects ?? []).map((s) => s.subject_id ?? s.custom_name?.trim()).filter(Boolean) as string[],
        grades: (teacherProfile.grades ?? []).map((g) => g.grade_id).filter(Boolean) as string[],
        institutions: (teacherProfile.institutions ?? [])
          .map((i) => (i.institution ? `${i.institution.type}: ${i.institution.name ?? i.institution_id}` : i.custom_name?.trim()))
          .filter(Boolean) as string[],
        city: teacherProfile.city ?? null,
        state: teacherProfile.state_province ?? null,
        country: teacherProfile.country_code ?? null,
        profile_completed_at: teacherProfile.profile_completed_at ?? null,
      }
    : {};

  const userProperties = {
    ...cachedProperties,
    ...teacherProfileFields,
    id: user?.id,
    email: typeof user?.email === 'string' ? user.email.toLowerCase() : null,
    authentication_provider: user?.authProvider,
    created_at: user?.created_at,
    confirmed_at: user?.confirmed_at,
    updated_at: user?.updated_at,
    email_confirmed_at: user?.email_confirmed_at,
    name: user?.name,
    plan: user?.plan,
    org: user?.org_id,
    is_demo: user?.is_demo,
    status: user?.status,
    user_role: user?.user_role,
    best_practices_completed: user?.best_practices_completed,
    student_friendly_name: user?.student_friendly_name,
    app_theme: appTheme,
    user_system_theme: userSystemTheme,
  };

  // flag bucketing passes a partial user with only an id, so a missing created_at is expected
  if (user?.created_at) {
    try {
      userProperties.user_age_days = getUserAgeDays(user.created_at);
    } catch (e) {
      logger.error('Error calculating user age days for amplitude user properties', e);
    }
  }

  return userProperties || {};
};

export const buildIdentifyObj = (identify: amplitude.Identify | Identify, userData: Partial<AnalyticsUserProperties>) => {
  for (const [key, value] of Object.entries(userData)) {
    if (value !== undefined && value !== null) {
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || Array.isArray(value)) {
        identify.set(key, value);
      } else if (typeof value === 'object') {
        // amplitude doesn't like nulls!
        const obj = omitBy(value, isNil) as Parameters<typeof identify.set>[1];
        identify.set(key, obj);
      }
    }
  }
};

export const stripNullAndUndefinedProperties = (obj: Partial<AnalyticsUserProperties>): ExperimentUser['user_properties'] => {
  // NOTE: I tried using omitBy from lodash but it didn't work with the ExperimentUser type
  const result: ExperimentUser['user_properties'] = {};

  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && value !== undefined) {
      result[key] = value;
    }
  }

  return result;
};
